import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import Swal from '../helpers/swal';
import { toast } from 'react-toastify';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import { CampaignStatusBadge, getCampaignStatusKey } from './campaignDetailUtils';
import { useCampaign } from './useCampaign';
import { toProperCase } from '../process/processQueueUtils';
const STOP_STATUS = 'ERROR';
const STOPPABLE_STATUSES = ['PENDING', 'IN_PROGRESS'];

function CampaignStop() {
    const { campaignId } = useParams();
    const { campaign, isLoading, error, refetch } = useCampaign(campaignId);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const statusKey = getCampaignStatusKey(campaign);
    const isStopped = statusKey === STOP_STATUS;
    const isCompleted = statusKey === 'COMPLETED';
    const canStop = STOPPABLE_STATUSES.includes(statusKey);

    const isSms = campaign?.type === 'SMS';
    const queueLabel = isSms ? 'SMS queue' : 'Email queue';

    const handleStopClick = () => {
        Swal.fire({
            title: 'Stop this campaign?',
            html: `
                <p class="mb-2">This will set the campaign status to <strong>Error</strong>.</p>
                <p class="mb-0 text-muted small">All waiting items in the ${queueLabel.toLowerCase()} will be marked as failed.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            confirmButtonText: 'Stop campaign',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;

            setIsSubmitting(true);
            campaignService
                .updateCampaign(campaignId, STOP_STATUS)
                .then(async () => {
                    await refetch();
                    toast.success('Campaign stopped successfully.', {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    });
                })
                .catch((err) => {
                    if (err?.response?.status === 403) {
                        toast.error('You do not have permission to stop this campaign.', {
                            position: toast.POSITION.BOTTOM_RIGHT,
                        });
                    } else {
                        toast.error('Failed to stop campaign. Please try again.', {
                            position: toast.POSITION.BOTTOM_RIGHT,
                        });
                    }
                })
                .finally(() => setIsSubmitting(false));
        });
    };

    return (
        <div className="container-fluid px-3 px-lg-4">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h1 className="mb-0">
                    <i className="bi bi-stop-circle me-2"></i>
                    Stop Campaign
                </h1>
                <Link to="/campaign" className="campaign-queue-page__back d-inline-flex align-items-center gap-1">
                    <i className="bi bi-arrow-left"></i>
                    Back to campaigns
                </Link>
            </div>

            {isLoading ? (
                <Loading />
            ) : error ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                campaign && (
                    <>
                        <div className="campaign-queue-context p-3 p-md-4 mb-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                <div className="flex-grow-1">
                                    <div className="text-muted small mb-1">{campaign.clinicName || '—'}</div>
                                    <div className="campaign-queue-context__title fw-semibold mb-1">
                                        {campaign.name || 'Untitled campaign'}
                                    </div>
                                    {campaign.title && (
                                        <div className="campaign-queue-context__meta text-muted mb-2">
                                            {campaign.title}
                                        </div>
                                    )}
                                    <div className="d-flex flex-wrap gap-2">
                                        <span
                                            className={`badge campaign-queue-type-badge ${
                                                isSms
                                                    ? 'bg-success bg-opacity-10 text-success'
                                                    : 'bg-primary bg-opacity-10 text-primary'
                                            }`}
                                        >
                                            <i
                                                className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-1`}
                                            ></i>
                                            {campaign.type}
                                        </span>
                                        <span className="badge bg-light text-dark border campaign-queue-type-badge">
                                            {toProperCase(campaign.category)}
                                        </span>
                                        <CampaignStatusBadge
                                            status={campaign.status}
                                            statusCode={campaign.statusCode}
                                        />
                                    </div>
                                </div>
                                <div className="text-md-end">
                                    <div className="text-muted small mb-1">Campaign ID</div>
                                    <div className="font-monospace small text-break">{campaign.id}</div>
                                </div>
                            </div>
                        </div>

                        {isStopped && (
                            <Alert variant="success" className="d-flex align-items-start gap-2">
                                <i className="bi bi-check-circle-fill mt-1"></i>
                                <div>
                                    <strong>This campaign has already been stopped.</strong>
                                    <div className="small mt-1 mb-0">
                                        No further sends will run while the status is Error.
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {isCompleted && (
                            <Alert variant="info" className="d-flex align-items-start gap-2">
                                <i className="bi bi-info-circle-fill mt-1"></i>
                                <div>
                                    <strong>This campaign has already completed.</strong>
                                    <div className="small mt-1 mb-0">
                                        Completed campaigns cannot be force-stopped.
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {canStop && (
                            <div className="card shadow-sm border-0 campaign-stop-card">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-start gap-3 mb-4">
                                        <div className="campaign-stop-card__icon">
                                            <i className="bi bi-exclamation-triangle-fill"></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-2" style={{ color: '#012970' }}>
                                                Force stop campaign
                                            </h5>
                                            <p className="text-muted mb-0">
                                                Use this only when you need to halt a running campaign
                                                immediately. Pending deliveries will be cancelled and the
                                                campaign will be marked as stopped.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="campaign-stop-card__effects mb-4">
                                        <div className="text-muted small text-uppercase mb-2">
                                            What happens when you stop
                                        </div>
                                        <ul className="mb-0 ps-3">
                                            <li className="mb-2">
                                                Campaign status is set to{' '}
                                                <CampaignStatusBadge status={STOP_STATUS} />
                                            </li>
                                            <li className="mb-2">
                                                All <strong>waiting</strong> items in the {queueLabel} are
                                                marked as failed
                                            </li>
                                            <li className="mb-0">
                                                Already sent messages are not reversed
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 pt-3 border-top">
                                        <div className="text-muted small">
                                            Current status:{' '}
                                            <CampaignStatusBadge
                                                status={campaign.status}
                                                statusCode={campaign.statusCode}
                                            />
                                        </div>
                                        <Button
                                            variant="danger"
                                            onClick={handleStopClick}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    Stopping...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-stop-circle me-1"></i>
                                                    Stop campaign
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!isStopped && !isCompleted && !canStop && statusKey && (
                            <Alert variant="secondary">
                                This campaign cannot be stopped from its current status (
                                <CampaignStatusBadge status={campaign.status} statusCode={campaign.statusCode} />
                                ).
                            </Alert>
                        )}
                    </>
                )
            )}
        </div>
    );
}

export default CampaignStop;
